import { useRef, useState } from 'react';
import { de } from '../../i18n/de';
import { isAcceptedImage, prepareImage } from '../../lib/image';
import { toast } from '../../state/toast';
import { useStoryEnv } from '../../state/storyEnv';
import { ImageDrop } from '../design/ImageDrop';

interface Props {
  label: string;
  /** Asset-ID oder null */
  value: string | null;
  onChange: (assetId: string | null) => void;
  /** Schlanke Darstellung für Listenzeilen: kleine Vorschau mit Knöpfen, kein Beschriftungsblock */
  compact?: boolean;
}

/**
 * Bildfeld: Die Bildfläche selbst wählt das Bild (klicken oder eine Datei hineinziehen); es wird verkleinert hochgeladen, gemerkt wird
 * die Bild-ID. Ein Klick auf ein vorhandenes Bild wählt ein anderes, das ✕ in der Ecke entfernt es wieder (danach gilt das Standardbild).
 */
export function ImageField({ label, value, onChange, compact }: Props) {
  if (compact) return <CompactImageField label={label} value={value} onChange={onChange} />;
  return (
    <div className="field image-field" role="group" aria-label={label}>
      <div className="label">{label}</div>
      <ImageDrop value={value} onChange={onChange} label={label} />
    </div>
  );
}

/** Listenzeile (z. B. Platzhalterbild einer Klasse): kleine Vorschau, Knöpfe zum Wählen und Entfernen */
function CompactImageField({ label, value, onChange }: Pick<Props, 'label' | 'value' | 'onChange'>) {
  const { actions } = useStoryEnv();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const pick = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    if (!isAcceptedImage(file)) return void toast.error(de.details.imageBad);
    setBusy(true);
    try {
      const asset = await actions.uploadAsset(await prepareImage(file));
      if (asset) onChange(asset.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : de.details.imageBad);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className="row" role="group" aria-label={label}>
      <div className="row">
        {value && <img className="thumb" src={`/api/assets/${value}`} alt="" width={28} height={28} />}
        <button type="button" className="btn small" onClick={() => input.current?.click()} disabled={busy} aria-label={`${label}: ${de.details.imageChoose}`}>
          {busy ? de.details.imageUploading : '🖼'}
        </button>
        {value && (
          <button type="button" className="btn small ghost" onClick={() => onChange(null)} aria-label={`${label}: ${de.details.imageRemove}`}>
            ✕
          </button>
        )}
        <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" aria-label={`${label}: Datei`} onChange={(e) => void pick(e.target.files?.[0])} />
      </div>
    </div>
  );
}
